import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./HealthDashboard.scss";
import useApi from "../../hook/useAPI";
import { HEALTH } from "../../utils/const";
import CommonTable from "../../components/Table/CommonTable";

const DELIVERY_LOG_COLUMNS = [
  { key: "date", header: "Time" },
  { key: "channel", header: "Channel" },
  { key: "deliveryStatus", header: "Status" },
  { key: "to", header: "To / SIM" },
  { key: "type", header: "Type" },
  { key: "message", header: "Message" },
  { key: "error", header: "Error" },
];

const statusClass = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "ok" || normalized === "success") return "health-card__value--ok";
  if (normalized === "fail" || normalized === "degraded" || normalized === "failure") {
    return "health-card__value--fail";
  }
  return "health-card__value--unknown";
};

const formatStatusLabel = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "ok" || normalized === "success") return "Good";
  if (normalized === "fail" || normalized === "failure") return "Bad";
  if (normalized === "degraded") return "Degraded";
  if (!value || normalized === "unknown") return "Unknown";
  return value;
};

const formatCheckLabel = (key) => key.charAt(0).toUpperCase() + key.slice(1);

const formatRate = (rate) => (rate == null ? "—" : `${rate}%`);

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "delivery", label: "Delivery logs" },
  { id: "cron", label: "Cron jobs" },
];

const HealthDashboard = () => {
  const { getData } = useApi();
  const [activeTab, setActiveTab] = useState("overview");
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [uptime, setUptime] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [cronHealth, setCronHealth] = useState(null);
  const [deliveryLogs, setDeliveryLogs] = useState(null);
  const [deliveryFilters, setDeliveryFilters] = useState({
    page: 1,
    limit: 10,
    channel: "",
    status: "",
  });
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadHealth = useCallback(async () => {
    try {
      const response = await getData(HEALTH.GET_HEALTH, null, false);
      setHealth(response);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setHealth(null);
      setError(err?.response?.data?.message || "Unable to reach API health endpoint");
    }
  }, [getData]);

  const loadStats = useCallback(async () => {
    try {
      const response = await getData(HEALTH.GET_ADMIN_STATS, null, false);
      setStats(response);
    } catch (err) {
      console.error("Admin stats error:", err);
    }
  }, [getData]);

  const loadUptime = useCallback(async () => {
    try {
      const response = await getData(HEALTH.GET_ADMIN_UPTIME, null, false);
      setUptime(response);
    } catch (err) {
      console.error("Admin uptime error:", err);
      setUptime(null);
    }
  }, [getData]);

  const loadIntegrations = useCallback(async () => {
    try {
      const response = await getData(HEALTH.GET_INTEGRATIONS_HEALTH, null, false);
      setIntegrations(response);
    } catch (err) {
      console.error("Integrations health error:", err);
      setIntegrations(null);
    }
  }, [getData]);

  const loadCronHealth = useCallback(async () => {
    try {
      const response = await getData(HEALTH.GET_CRON_HEALTH, null, false);
      setCronHealth(response);
    } catch (err) {
      console.error("Cron health error:", err);
      setCronHealth(null);
    }
  }, [getData]);

  const loadDeliveryLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(deliveryFilters.page),
        limit: String(deliveryFilters.limit),
      });
      if (deliveryFilters.channel) {
        params.set("channel", deliveryFilters.channel);
      }
      if (deliveryFilters.status) {
        params.set("status", deliveryFilters.status);
      }
      const response = await getData(
        `${HEALTH.GET_DELIVERY_LOGS}?${params.toString()}`,
        null,
        false,
      );
      setDeliveryLogs(response);
    } catch (err) {
      console.error("Delivery logs error:", err);
      setDeliveryLogs(null);
    }
  }, [deliveryFilters, getData]);

  const refresh = useCallback(async () => {
    await Promise.all([
      loadHealth(),
      loadStats(),
      loadUptime(),
      loadIntegrations(),
      loadCronHealth(),
      loadDeliveryLogs(),
    ]);
  }, [loadHealth, loadStats, loadUptime, loadIntegrations, loadCronHealth, loadDeliveryLogs]);

  useEffect(() => {
    refresh();
    const intervalId = setInterval(refresh, 60000);
    return () => clearInterval(intervalId);
  }, [refresh]);

  useEffect(() => {
    loadDeliveryLogs();
  }, [loadDeliveryLogs]);

  const failingServices = health?.checks
    ? Object.entries(health.checks).filter(([, value]) => value !== "ok")
    : [];

  const renderOverview = () => (
    <div className="health-dashboard__grid">
      <div className="health-card">
        <span className="health-card__label">Overall status</span>
        <span className={`health-card__value ${statusClass(health?.status)}`}>
          {formatStatusLabel(health?.status)}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">API</span>
        <span className={`health-card__value ${statusClass(health?.checks?.api)}`}>
          {formatStatusLabel(health?.checks?.api)}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">MongoDB</span>
        <span className={`health-card__value ${statusClass(health?.checks?.mongo)}`}>
          {formatStatusLabel(health?.checks?.mongo)}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">TCP GPS port</span>
        <span className={`health-card__value ${statusClass(health?.checks?.tcp)}`}>
          {formatStatusLabel(health?.checks?.tcp)}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">Email provider (SendGrid)</span>
        <span className={`health-card__value ${statusClass(integrations?.email?.status)}`}>
          {formatStatusLabel(integrations?.email?.status)}
        </span>
        <span className="health-card__meta">
          {integrations?.email?.configured
            ? `24h: ${integrations.email.success}/${integrations.email.total} ok (${formatRate(integrations.email.successRate)})`
            : "Not configured"}
        </span>
        {integrations?.email?.lastError && (
          <span className="health-card__meta health-card__meta--error">
            {integrations.email.lastError}
          </span>
        )}
      </div>

      <div className="health-card">
        <span className="health-card__label">SMS provider (Twilio)</span>
        <span className={`health-card__value ${statusClass(integrations?.sms?.status)}`}>
          {formatStatusLabel(integrations?.sms?.status)}
        </span>
        <span className="health-card__meta">
          {integrations?.sms?.configured
            ? `24h: ${integrations.sms.success}/${integrations.sms.total} ok (${formatRate(integrations.sms.successRate)})`
            : "Not configured"}
        </span>
        {integrations?.sms?.lastError && (
          <span className="health-card__meta health-card__meta--error">
            {integrations.sms.lastError}
          </span>
        )}
      </div>

      <div className="health-card">
        <span className="health-card__label">Device SMS (1NCE)</span>
        <span className={`health-card__value ${statusClass(integrations?.deviceSms?.status)}`}>
          {formatStatusLabel(integrations?.deviceSms?.status)}
        </span>
        <span className="health-card__meta">
          {integrations?.deviceSms?.configured
            ? `24h: ${integrations.deviceSms.success}/${integrations.deviceSms.total} ok (${formatRate(integrations.deviceSms.successRate)})`
            : "Not configured"}
        </span>
        {integrations?.deviceSms?.lastError && (
          <span className="health-card__meta health-card__meta--error">
            {integrations.deviceSms.lastError}
          </span>
        )}
      </div>

      <div className="health-card">
        <span className="health-card__label">Last health check</span>
        <span className="health-card__value health-card__value--neutral">
          {health?.timestamp ? new Date(health.timestamp).toLocaleString() : "—"}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">Dashboard refreshed</span>
        <span className="health-card__value health-card__value--neutral">
          {lastRefresh ? lastRefresh.toLocaleString() : "—"}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">Active devices (24h)</span>
        <span className="health-card__value health-card__value--neutral">
          {stats?.statusesLast24h ?? "—"}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">Total users</span>
        <span className="health-card__value health-card__value--neutral">
          {stats?.users ?? "—"}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">Total devices</span>
        <span className="health-card__value health-card__value--neutral">
          {stats?.devices ?? "—"}
        </span>
      </div>

      <div className="health-card">
        <span className="health-card__label">Uptime % (external)</span>
        <span className="health-card__value health-card__value--neutral">
          {!uptime?.configured
            ? "Not configured"
            : uptime?.error
              ? uptime.error
              : uptime?.uptimePercent != null
                ? `${uptime.uptimePercent}% (${uptime.uptimeDays || 30}d)`
                : "—"}
        </span>
      </div>

      <div className="health-card health-card--wide">
        <span className="health-card__label">Recent incidents (UptimeRobot)</span>
        {uptime?.configured && uptime?.incidents?.length > 0 ? (
          <ul className="health-card__list health-card__list--neutral">
            {uptime.incidents.slice(0, 5).map((incident) => (
              <li key={`${incident.monitorId}-${incident.datetime}`}>
                <strong>{incident.monitorName}</strong>
                {incident.datetime ? ` — ${new Date(incident.datetime).toLocaleString()}` : ""}
                {incident.reason ? ` (${incident.reason})` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <span className="health-card__value health-card__value--neutral">No incidents found</span>
        )}
      </div>

      {uptime?.configured && uptime?.monitors?.length > 0 && (
        <div className="health-card health-card--wide">
          <span className="health-card__label">External monitors</span>
          <ul className="health-card__list health-card__list--neutral">
            {uptime.monitors.map((monitor) => (
              <li key={monitor.id}>
                {monitor.friendlyName}:{" "}
                <strong className={monitor.statusCode === 2 ? "health-inline--ok" : "health-inline--fail"}>
                  {monitor.status}
                </strong>
                {monitor.uptimePercent != null ? ` — ${monitor.uptimePercent}%` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="health-card health-card--wide">
        <span className="health-card__label">Failing services</span>
        {failingServices.length === 0 ? (
          <span className="health-card__value health-card__value--ok">None</span>
        ) : (
          <ul className="health-card__list">
            {failingServices.map(([key, value]) => (
              <li key={key}>
                {formatCheckLabel(key)}: <strong>{formatStatusLabel(value)}</strong>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const deliveryLogRows = useMemo(() => {
    if (!deliveryLogs?.logs?.length) {
      return [];
    }
    return deliveryLogs.logs.map((log) => ({
      id: log._id,
      rowStatus: log.status,
      date: log.createdAt,
      channel: log.channel,
      deliveryStatus: (
        <span className={`health-delivery-status ${statusClass(log.status)}`}>
          {log.status}
        </span>
      ),
      to: log.to,
      type: log.notificationType || log.source || "—",
      message: (log.messagePreview || log.subject || "").slice(0, 80),
      error: log.errorMessage || "—",
    }));
  }, [deliveryLogs?.logs]);

  const getDeliveryRowClassName = (row) => {
    if (row.rowStatus === "success") {
      return "delivery-log-row--success";
    }
    if (row.rowStatus === "failure") {
      return "delivery-log-row--failure";
    }
    return "";
  };

  const handleDeliveryPageChange = (page) => {
    setDeliveryFilters((prev) => ({ ...prev, page }));
  };

  const handleDeliveryItemsPerPageChange = (event) => {
    setDeliveryFilters((prev) => ({
      ...prev,
      limit: Number(event.target.value),
      page: 1,
    }));
  };

  const renderDeliveryLogs = () => (
    <div className="health-dashboard__panel">
      <div className="health-dashboard__filters">
        <select
          value={deliveryFilters.channel}
          onChange={(e) => setDeliveryFilters((prev) => ({
            ...prev,
            channel: e.target.value,
            page: 1,
          }))}
        >
          <option value="">All channels</option>
          <option value="email">Email</option>
          <option value="sms">SMS (Twilio)</option>
          <option value="device_sms">Device SMS (1NCE)</option>
        </select>
        <select
          value={deliveryFilters.status}
          onChange={(e) => setDeliveryFilters((prev) => ({
            ...prev,
            status: e.target.value,
            page: 1,
          }))}
        >
          <option value="">All statuses</option>
          <option value="success">Success</option>
          <option value="failure">Failure</option>
        </select>
      </div>

      <div className="health-dashboard__table-section">
        <CommonTable
          columns={DELIVERY_LOG_COLUMNS}
          rows={deliveryLogRows}
          isPagination
          pageName="delivery-logs"
          showHeaderWhenEmpty
          hidePaginationWhenEmpty={false}
          totalPages={deliveryLogs?.totalPages || 1}
          currentPage={deliveryFilters.page}
          itemsPerPage={deliveryFilters.limit}
          totalCount={deliveryLogs?.total ?? 0}
          onPageChange={handleDeliveryPageChange}
          onItemsPerPageChange={handleDeliveryItemsPerPageChange}
          getRowClassName={getDeliveryRowClassName}
          emptyState={{
            title: "No delivery logs yet",
            message: "Logs are recorded when email or SMS is sent.",
          }}
        />
      </div>
    </div>
  );

  const renderCronHealth = () => (
    <div className="health-dashboard__grid">
      {cronHealth?.jobs?.length ? (
        cronHealth.jobs.map((job) => (
          <div className="health-card" key={job.jobId}>
            <span className="health-card__label">{job.jobName}</span>
            <span className={`health-card__value ${statusClass(job.lastStatus)}`}>
              {formatStatusLabel(job.lastStatus || "unknown")}
            </span>
            <span className="health-card__meta">Schedule: {job.schedule || "—"}</span>
            <span className="health-card__meta">
              Last run: {job.lastRunAt ? new Date(job.lastRunAt).toLocaleString() : "—"}
            </span>
            <span className="health-card__meta">
              24h: {job.last24h?.success ?? 0} ok / {job.last24h?.failure ?? 0} fail
              ({formatRate(job.last24h?.successRate)})
            </span>
            {job.lastError && (
              <span className="health-card__meta health-card__meta--error">{job.lastError}</span>
            )}
          </div>
        ))
      ) : (
        <div className="health-card health-card--wide">
          <span className="health-card__value health-card__value--neutral">
            Cron jobs register when the API server starts. No runs recorded yet.
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="health-dashboard">
      <div className="health-dashboard__header">
        <h2>System Health</h2>
        <button type="button" className="health-dashboard__refresh" onClick={refresh}>
          Refresh
        </button>
      </div>

      <div className="health-dashboard__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`health-dashboard__tab ${activeTab === tab.id ? "health-dashboard__tab--active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="health-dashboard__alert health-dashboard__alert--error">
          {error}
        </div>
      )}

      {activeTab === "overview" && renderOverview()}
      {activeTab === "delivery" && renderDeliveryLogs()}
      {activeTab === "cron" && renderCronHealth()}
    </div>
  );
};

export default HealthDashboard;
